import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { siteConfig } from "@/data/siteConfig";
import { Reveal } from "./shared";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, label: "Scholar Information" },
  { id: 2, label: "Academic Background" },
  { id: 3, label: "Goals & Responses" },
  { id: 4, label: "Parent / Guardian" },
  { id: 5, label: "Review & Submit" },
];

const empty = {
  full_name: "", email: "", phone: "", date_of_birth: "", city_state: "",
  school: "", grade_level: "", gpa: "",
  why_join: "", career_interests: "", leadership_experience: "",
  guardian_name: "", guardian_email: "", guardian_phone: "",
};

const Field = ({ label, name, value, onChange, type = "text", required, placeholder, testid }) => (
  <label className="block">
    <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-charcoal/60">
      {label}{required && <span className="text-gold"> *</span>}
    </span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border border-navy/20 bg-white px-4 py-3 text-navy outline-none transition-colors duration-200 focus:border-gold placeholder:text-navy/30"
    />
  </label>
);

const Area = ({ label, name, value, onChange, required, testid }) => (
  <label className="block">
    <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-charcoal/60">
      {label}{required && <span className="text-gold"> *</span>}
    </span>
    <textarea
      data-testid={testid}
      name={name}
      value={value}
      onChange={onChange}
      rows={4}
      className="w-full resize-none border border-navy/20 bg-white px-4 py-3 text-navy outline-none transition-colors duration-200 focus:border-gold"
    />
  </label>
);

export const ApplySection = () => {
  const { apply } = siteConfig;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.email || !form.phone) {
        toast.error("Please complete name, email, and phone.");
        return false;
      }
    }
    if (step === 2 && (!form.school || !form.grade_level)) {
      toast.error("Please provide your school and grade level.");
      return false;
    }
    if (step === 3 && !form.why_join) {
      toast.error("Please tell us why you want to join.");
      return false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(5, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications`, form);
      setDone(data.reference);
      toast.success("Application received.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" data-testid="apply-section" className="bg-cream py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">{apply.eyebrow}</p>
              <h2 className="font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">{apply.heading}</h2>
              <p className="mt-6 text-base leading-relaxed text-charcoal/75">{apply.intro}</p>
              <dl className="mt-10 space-y-6 border-t border-navy/10 pt-8">
                <div><dt className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Who should apply</dt><dd className="text-navy">{apply.who}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Commitment</dt><dd className="text-navy">{apply.commitment}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Deadline</dt><dd className="text-navy">{apply.deadline}</dd></div>
              </dl>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <div className="border border-navy/10 bg-white p-8 lg:p-12">
              {done ? (
                <div data-testid="apply-success" className="py-10 text-center">
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center bg-gold text-navy"><Check size={32} /></div>
                  <h3 className="font-display text-3xl text-navy">Application received</h3>
                  <p className="mt-4 text-charcoal/75">Thank you for applying to {siteConfig.orgName}. Your reference number is:</p>
                  <p data-testid="apply-reference" className="mt-3 font-display text-2xl text-gold">{done}</p>
                  <p className="mt-4 text-sm text-charcoal/60">We review applications on a rolling basis and will be in touch by email.</p>
                </div>
              ) : (
                <>
                  <div className="mb-10 flex items-center gap-2">
                    {steps.map((s) => (
                      <div key={s.id} className="flex-1">
                        <div className={`h-1 w-full transition-colors duration-300 ${s.id <= step ? "bg-gold" : "bg-navy/10"}`} />
                      </div>
                    ))}
                  </div>
                  <div className="mb-8 flex items-baseline justify-between">
                    <h3 className="font-display text-2xl text-navy">{steps[step - 1].label}</h3>
                    <span className="text-xs uppercase tracking-[0.2em] text-charcoal/50">Step {step} / 5</span>
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.35 }}
                      className="space-y-5"
                    >
                      {step === 1 && (
                        <>
                          <Field testid="f-full_name" label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required />
                          <div className="grid gap-5 sm:grid-cols-2">
                            <Field testid="f-email" label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                            <Field testid="f-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} required />
                          </div>
                          <div className="grid gap-5 sm:grid-cols-2">
                            <Field testid="f-dob" label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth} onChange={onChange} />
                            <Field testid="f-city" label="City & State" name="city_state" value={form.city_state} onChange={onChange} placeholder="Roanoke, VA" />
                          </div>
                        </>
                      )}
                      {step === 2 && (
                        <>
                          <Field testid="f-school" label="Current School" name="school" value={form.school} onChange={onChange} required />
                          <div className="grid gap-5 sm:grid-cols-2">
                            <Field testid="f-grade" label="Grade Level" name="grade_level" value={form.grade_level} onChange={onChange} required placeholder="e.g. 11th Grade" />
                            <Field testid="f-gpa" label="GPA (optional)" name="gpa" value={form.gpa} onChange={onChange} />
                          </div>
                        </>
                      )}
                      {step === 3 && (
                        <>
                          <Area testid="f-why" label="Why do you want to join the Club?" name="why_join" value={form.why_join} onChange={onChange} required />
                          <Field testid="f-career" label="Career Interests" name="career_interests" value={form.career_interests} onChange={onChange} />
                          <Area testid="f-leadership" label="Describe a time you showed leadership" name="leadership_experience" value={form.leadership_experience} onChange={onChange} />
                        </>
                      )}
                      {step === 4 && (
                        <>
                          <Field testid="f-gname" label="Parent / Guardian Name" name="guardian_name" value={form.guardian_name} onChange={onChange} />
                          <div className="grid gap-5 sm:grid-cols-2">
                            <Field testid="f-gemail" label="Guardian Email" name="guardian_email" type="email" value={form.guardian_email} onChange={onChange} />
                            <Field testid="f-gphone" label="Guardian Phone" name="guardian_phone" value={form.guardian_phone} onChange={onChange} />
                          </div>
                          <p className="text-sm text-charcoal/60">If you are 18 or older, you may leave this section blank.</p>
                        </>
                      )}
                      {step === 5 && (
                        <div className="divide-y divide-navy/10 border border-navy/10">
                          {Object.entries({
                            "Full Name": form.full_name, Email: form.email, Phone: form.phone,
                            "City & State": form.city_state, School: form.school, "Grade Level": form.grade_level,
                            GPA: form.gpa, "Why Join": form.why_join, "Career Interests": form.career_interests,
                            Guardian: form.guardian_name,
                          }).map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-6 px-5 py-3 text-sm">
                              <span className="text-charcoal/60">{k}</span>
                              <span className="text-right font-medium text-navy">{v || "—"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <div className="mt-10 flex items-center justify-between">
                    <button
                      data-testid="apply-back-btn"
                      onClick={back}
                      disabled={step === 1}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-navy disabled:opacity-30"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    {step < 5 ? (
                      <button
                        data-testid="apply-next-btn"
                        onClick={next}
                        className="group inline-flex items-center gap-2 bg-navy px-8 py-3 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-gold hover:text-navy"
                      >
                        Continue <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    ) : (
                      <button
                        data-testid="apply-submit-btn"
                        onClick={submit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-gold px-8 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-cream disabled:opacity-60"
                      >
                        {submitting ? "Submitting…" : "Submit Application"}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
